import { useState, type FormEvent } from 'react';
import { useActionLock } from '@breaker/action-lock';
import { Link } from '@tanstack/react-router';
import { BillingPage } from '@/pages/billing';
import { useActiveFleet } from '@/data/fleets';
import {
  useMailIdentity,
  useSetPortalSlug,
  useTariff,
  useUpdateFleet,
  type TariffDefaults,
} from '@/data/fleets';
import {
  isInviteLive,
  useDriverInvites,
  useInviteDriver,
  useRevokeDriverInvite,
  useTeamList,
} from '@/data/drivers';
import { BadgeChips, ComplianceBadge } from '@/pages/team-member-detail';
import {
  useCheckAdminDomain,
  useCheckPortalDomain,
  useClaimAdminDomain,
  useClaimPortalDomain,
  usePortalDomains,
  useRemovePortalDomain,
  type ExpectedDnsRecords,
} from '@/data/portal-domains';
import {
  useCreateDomainOrder,
  useDomainOrders,
  useDomainOrdersConfig,
  useDomainSearch,
  type DomainCandidate,
  type DomainRegistrantInput,
  type DomainSearchParsed,
} from '@/data/domain-orders';
import { RateCardEditor, seedTariffForm } from '@/components/rate-card-editor';
import { BrandEditor } from '@/components/brand-editor';
import { ManagedDomainDns } from '@/components/managed-domain-dns';
import {
  DomainResultsList,
  RegistrationTermsConsent,
  TldPriceChips,
  WhoisPrivacyControl,
  domainQueryInputProps,
} from '@/components/wizard-domain-step';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { TableScroll } from '@/components/ui/table-scroll';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/auth/api';
import { formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';
import type {
  DriverEngagementType,
  DriverInvite,
  DriverListEntry,
  Fleet,
  FleetRole,
  PortalDomain,
} from '@breaker/shared-types';
import type { InvitableBadge } from '@breaker/shared-schemas';

const ROLE_LABEL: Record<FleetRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  dispatcher: 'Dispatcher',
  driver: 'Driver',
  accountant: 'Accountant',
};

// ============================================================
// Settings information architecture (IA unit): a hub index of cards, each
// area on its own route. Mobile-first (390px: stacked cards; each sub-page
// carries a breadcrumb back). The sub-pages are thin wrappers around the
// same components the single page used — zero behaviour change.
// ============================================================

function PageSpinner() {
  return (
    <div className="flex justify-center py-24">
      <Spinner size="lg" />
    </div>
  );
}

/** Breadcrumb + title chrome shared by every Settings sub-page. */
function SubPageShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="px-4 sm:px-8 py-8 sm:py-10 max-w-[900px] mx-auto">
      <Link
        to="/settings"
        className="inline-flex items-center gap-1 text-sm text-breaker-mute hover:text-breaker-identity transition-colors mb-3"
      >
        ← Settings
      </Link>
      <h1 className="text-2xl font-semibold tracking-tight text-breaker-ink mb-6">{title}</h1>
      {children}
    </div>
  );
}

function useSettingsCanEdit(): boolean {
  // 0061 RBAC: owners and admins edit settings; everyone else reads.
  const { role } = useActiveFleet();
  return role === 'owner' || role === 'admin';
}

const HUB_CARDS: Array<{ to: string; title: string; desc: string }> = [
  { to: '/settings/company', title: 'Company', desc: 'Legal name and VAT status.' },
  {
    to: '/settings/domains',
    title: 'Your portal address',
    desc: 'Free subdomain, buy your own, or connect one you own — plus your admin address.',
  },
  { to: '/settings/brand', title: 'Brand', desc: 'Colours, logo, and how your portal email sends.' },
  { to: '/settings/rates', title: 'Rate card', desc: 'The prices your booking page quotes.' },
  { to: '/settings/team', title: 'Team', desc: 'Everyone in your fleet — badges, invites, drivers.' },
  { to: '/settings/billing', title: 'Billing', desc: 'Subscription, card, and invoices.' },
];

/** /settings — the hub index. */
export function SettingsPage() {
  const { fleet, role } = useActiveFleet();
  const canEdit = role === 'owner' || role === 'admin';
  if (!fleet) return <PageSpinner />;

  return (
    <div className="px-4 sm:px-8 py-8 sm:py-10 max-w-[900px] mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-breaker-ink">Settings</h1>
        <p className="text-sm text-breaker-mute mt-1">
          {fleet.legalName}
          {role ? <> · {ROLE_LABEL[role]}</> : null}
        </p>
      </header>

      <div className="space-y-3">
        {HUB_CARDS.map((h) => (
          <Link
            key={h.to}
            to={h.to}
            className="block rounded-lg border border-breaker-line p-4 hover:bg-breaker-ground-2 transition-colors"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-breaker-ink">{h.title}</p>
                <p className="text-xs text-breaker-mute mt-0.5">{h.desc}</p>
              </div>
              <span className="text-breaker-mute shrink-0" aria-hidden>
                →
              </span>
            </div>
          </Link>
        ))}
      </div>

      {/* Features: entitlements + the portal on/off toggle — small, kept
          inline on the hub rather than its own route. */}
      <div className="mt-8">
        <Card title="Features">
          <FeaturesSettings fleet={fleet} canEdit={canEdit} />
        </Card>
      </div>
    </div>
  );
}

/** /settings/company */
export function SettingsCompanyPage() {
  const { fleet } = useActiveFleet();
  const canEdit = useSettingsCanEdit();
  if (!fleet) return <PageSpinner />;
  return (
    <SubPageShell title="Company">
      <CompanySettings fleet={fleet} canEdit={canEdit} />
    </SubPageShell>
  );
}

/** /settings/rates */
export function SettingsRatesPage() {
  const { fleet } = useActiveFleet();
  const canEdit = useSettingsCanEdit();
  if (!fleet) return <PageSpinner />;
  return (
    <SubPageShell title="Rate card">
      <RateCardSettings fleetId={fleet.id} canEdit={canEdit} />
    </SubPageShell>
  );
}

/** /settings/brand — colours + logo + how portal email sends. */
export function SettingsBrandPage() {
  const { fleet } = useActiveFleet();
  const canEdit = useSettingsCanEdit();
  const mailIdentity = useMailIdentity(fleet?.id ?? '');
  if (!fleet) return <PageSpinner />;
  return (
    <SubPageShell title="Brand">
      <div className="space-y-6">
        <BrandEditor fleet={fleet} canEdit={canEdit} />
        <div className="border-t border-breaker-line pt-4">
          <DetailRow label="Portal mail sends as" last>
            {mailIdentity.isLoading ? (
              '…'
            ) : mailIdentity.data ? (
              <span>
                <span className="ref">{mailIdentity.data.from}</span>{' '}
                <Badge tone={mailIdentity.data.configured ? 'success' : 'neutral'}>
                  {mailIdentity.data.configured ? 'Custom domain' : 'Platform default'}
                </Badge>
              </span>
            ) : (
              '—'
            )}
          </DetailRow>
        </div>
      </div>
    </SubPageShell>
  );
}

/** /settings/domains — portal address (slug + three doors + managed panel)
 *  and the admin sign-in domain. Unit B (DNS/email management) lands here. */
export function SettingsDomainsPage() {
  const { fleet } = useActiveFleet();
  const canEdit = useSettingsCanEdit();
  if (!fleet) return <PageSpinner />;
  return (
    <SubPageShell title="Your portal address">
      <div className="space-y-8">
        <PortalSlugSettings fleet={fleet} canEdit={canEdit} />
        <div className="border-t border-breaker-line pt-6">
          <PortalAddressSettings fleet={fleet} fleetId={fleet.id} canEdit={canEdit} />
        </div>
        <div className="border-t border-breaker-line pt-6">
          <h2 className="text-sm font-semibold text-breaker-ink mb-3">Admin sign-in domain</h2>
          <AdminDomainSettings fleetId={fleet.id} canEdit={canEdit} />
        </div>
      </div>
    </SubPageShell>
  );
}

/** /settings/team — THE people surface (Team unit): every person one row,
 *  live invites as pseudo-rows on top, one invite rail. */
export function SettingsTeamPage() {
  const { fleet, role } = useActiveFleet();
  const fleetId = fleet?.id ?? '';
  // Matrix: dispatchers invite drivers; admins run the team; server enforces.
  const canInvite = role === 'owner' || role === 'admin' || role === 'dispatcher';
  const teamQuery = useTeamList(fleetId || null);
  const invitesQuery = useDriverInvites(fleetId || null);
  const [inviteOpen, setInviteOpen] = useState(false);
  if (!fleet) return <PageSpinner />;

  const liveInvites = (invitesQuery.data ?? []).filter(isInviteLive);

  return (
    <SubPageShell title="Team">
      <Card
        title="People"
        action={
          canInvite ? (
            <Button size="sm" onClick={() => setInviteOpen(true)}>
              Invite person
            </Button>
          ) : undefined
        }
      >
        {teamQuery.isLoading || invitesQuery.isLoading ? (
          <div className="py-6 flex justify-center">
            <Spinner />
          </div>
        ) : teamQuery.isError ? (
          <p className="text-sm text-breaker-red">Couldn&rsquo;t load the team.</p>
        ) : (
          <TeamTable
            fleetId={fleetId}
            members={teamQuery.data ?? []}
            invites={liveInvites}
            canInvite={canInvite}
          />
        )}
      </Card>

      <InvitePersonDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        fleetId={fleetId}
        actorRole={role}
      />
    </SubPageShell>
  );
}

/** /settings/billing — the ONE billing surface (ruling 2026-08-15): the full
 *  billing page + the VAT-registration toggle. Top-nav /billing redirects
 *  here, so old links and the trial banner keep working. */
export function SettingsBillingPage() {
  const { fleet, role } = useActiveFleet();
  if (!fleet) return <PageSpinner />;
  return (
    <div>
      <div className="px-4 sm:px-8 pt-6 max-w-[720px] mx-auto">
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 text-sm text-breaker-mute hover:text-breaker-identity transition-colors"
        >
          ← Settings
        </Link>
      </div>
      <BillingPage />
      <div className="px-4 sm:px-8 pb-10 max-w-[720px] mx-auto">
        <div className="border-t border-breaker-line pt-4">
          <h2 className="text-sm font-semibold text-breaker-ink mb-2">VAT registration</h2>
          <BillingSettings
            fleetId={fleet.id}
            vatRegistered={fleet.vatRegistered}
            canEdit={role === 'owner'}
          />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Team table — every person one row; live invites as pseudo-rows on top
// (the old Drivers-page pattern: a member row cannot pre-exist its user).
// ============================================================

const ROLE_SORT: Record<FleetRole, number> = {
  owner: 0,
  admin: 1,
  dispatcher: 2,
  accountant: 3,
  driver: 4,
};

function TeamTable({
  fleetId,
  members,
  invites,
  canInvite,
}: {
  fleetId: string;
  members: DriverListEntry[];
  invites: DriverInvite[];
  canInvite: boolean;
}) {
  const toast = useToast();
  const revoke = useRevokeDriverInvite(fleetId || null);

  if (members.length === 0 && invites.length === 0) {
    return (
      <p className="text-sm text-breaker-mute py-4 text-center">
        Nobody here yet — invite your first person.
      </p>
    );
  }

  const sorted = [...members].sort((a, b) => {
    const rank = (m: DriverListEntry) =>
      Math.min(...m.roles.map((r) => ROLE_SORT[r] ?? 9));
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    return a.displayName.localeCompare(b.displayName);
  });

  return (
    <TableScroll>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left">
            <Th>Name</Th>
            <Th>Badges</Th>
            <Th className="w-[130px]">Status</Th>
            <Th className="w-[140px]">Compliance</Th>
            <Th className="w-[140px]">Joined</Th>
          </tr>
        </thead>
        <tbody>
          {invites.map((invite) => (
            <tr key={invite.id} className="border-t border-breaker-line">
              <Td>
                <p className="font-medium text-breaker-ink">{invite.name}</p>
                <p className="text-xs text-breaker-mute">
                  {invite.email} · code <span className="ref">{invite.code}</span>
                </p>
              </Td>
              <Td>
                <BadgeChips roles={invite.roles} />
              </Td>
              <Td>
                <Badge tone="active">Invited</Badge>
              </Td>
              <Td className="text-xs text-breaker-mute">
                expires {formatRelative(invite.expiresAt)}
              </Td>
              <Td className="text-right">
                {canInvite ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={revoke.isPending && revoke.variables === invite.id}
                    action={() =>
                      revoke
                        .mutateAsync(invite.id)
                        .then(() => toast.success('Invite revoked'))
                        .catch((err) =>
                          toast.error(
                            err instanceof ApiError ? err.message : 'Revoke failed',
                          ),
                        )
                    }
                  >
                    Revoke
                  </Button>
                ) : null}
              </Td>
            </tr>
          ))}
          {sorted.map((m) => (
            <tr key={m.memberId} className="border-t border-breaker-line">
              <Td>
                <Link
                  to="/settings/team/$memberId"
                  params={{ memberId: m.memberId }}
                  className="font-medium text-breaker-ink hover:text-breaker-identity transition-colors"
                >
                  {m.displayName}
                </Link>
                <p className="text-xs text-breaker-mute">
                  @{m.handle}
                  {m.email ? ` · ${m.email}` : ''}
                </p>
              </Td>
              <Td>
                <BadgeChips roles={m.roles} />
              </Td>
              <Td>
                {m.status === 'active' ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Badge tone="success">Active</Badge>
                    {m.roles.includes('driver') && !m.isAvailable ? (
                      <span
                        className="h-2 w-2 rounded-full bg-breaker-line-2"
                        title="Not open to offers"
                      />
                    ) : null}
                  </span>
                ) : (
                  <Badge tone="danger">Disabled</Badge>
                )}
              </Td>
              <Td>
                {m.roles.includes('driver') ? (
                  <span className="inline-flex items-center gap-1.5">
                    <ComplianceBadge state={m.compliance} />
                    {m.compliancePendingCheck ? <Badge tone="active">Check</Badge> : null}
                  </span>
                ) : (
                  <span className="text-breaker-mute">—</span>
                )}
              </Td>
              <Td className="text-breaker-ink-2 text-xs">
                {m.joinedAt ? formatRelative(m.joinedAt) : '—'}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

// ============================================================
// Invite dialog — the ONE invite rail: badges chosen at invite time,
// engagement appears when Driver is ticked, sent via the fleet identity.
// ============================================================

const BADGE_OPTIONS: Array<{
  badge: InvitableBadge;
  label: string;
  hint: string;
  ownerOnly?: boolean;
  adminOnly?: boolean;
}> = [
  { badge: 'driver', label: 'Driver', hint: 'Operates loads. +£20/month while active.' },
  {
    badge: 'dispatcher',
    label: 'Dispatcher',
    hint: 'Schedules and assigns loads.',
    adminOnly: true,
  },
  {
    badge: 'accountant',
    label: 'Accountant',
    hint: 'Billing and invoices only.',
    adminOnly: true,
  },
  {
    badge: 'admin',
    label: 'Admin',
    hint: 'Edits fleet settings and runs the team.',
    ownerOnly: true,
  },
];

function InvitePersonDialog({
  open,
  onClose,
  fleetId,
  actorRole,
}: {
  open: boolean;
  onClose(): void;
  fleetId: string;
  actorRole: FleetRole | null;
}) {
  const toast = useToast();
  const create = useInviteDriver(fleetId || null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [badges, setBadges] = useState<InvitableBadge[]>(['driver']);
  const [engagement, setEngagement] = useState<DriverEngagementType>('subcontractor');
  const [error, setError] = useState<string | null>(null);
  // Unit D: Enter-key re-submits are dropped while one is in flight.
  const submitLock = useActionLock();
  const busy = create.isPending || submitLock.locked;

  const isOwner = actorRole === 'owner';
  const isAdmin = actorRole === 'owner' || actorRole === 'admin';
  const hasDriver = badges.includes('driver');

  function toggleBadge(b: InvitableBadge, on: boolean) {
    setBadges((prev) => {
      const next = new Set(prev);
      if (on) next.add(b);
      else next.delete(b);
      return [...next];
    });
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    if (badges.length === 0) {
      setError('Choose at least one badge.');
      return;
    }
    submitLock.run(() =>
      create
        .mutateAsync({
          name: name.trim(),
          email: email.trim(),
          phoneE164: phone.trim() || null,
          roles: badges,
          ...(hasDriver ? { engagementType: engagement } : {}),
        })
        .then(({ invite }) => {
          toast.success(`Invite sent to ${invite.email} — code ${invite.code}`);
          setName('');
          setEmail('');
          setPhone('');
          setBadges(['driver']);
          onClose();
        })
        .catch((err) => {
          setError(err instanceof ApiError ? err.message : 'Could not send the invite.');
        }),
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Invite a person"
      description="Choose their badges now — the invite is emailed under your fleet's own sending identity and is valid for 7 days."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Name" htmlFor="invName">
            <Input id="invName" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </Field>
          <Field label="Email" htmlFor="invEmail">
            <Input
              id="invEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Mobile (optional)" htmlFor="invPhone">
          <Input
            id="invPhone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+447700900000"
          />
        </Field>

        <fieldset>
          <legend className="text-2xs font-medium text-breaker-ink-2 uppercase tracking-wider mb-2">
            Badges
          </legend>
          <div className="space-y-2">
            {BADGE_OPTIONS.map((o) => {
              const locked = (o.ownerOnly && !isOwner) || (o.adminOnly && !isAdmin);
              return (
                <label
                  key={o.badge}
                  className={cn(
                    'flex items-start gap-3 rounded-md border border-breaker-line p-3',
                    locked && 'opacity-50',
                  )}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 mt-0.5"
                    checked={badges.includes(o.badge)}
                    disabled={Boolean(locked) || busy}
                    onChange={(ev) => toggleBadge(o.badge, ev.target.checked)}
                  />
                  <span>
                    <span className="block text-sm font-medium text-breaker-ink">{o.label}</span>
                    <span className="block text-xs text-breaker-mute">
                      {o.hint}
                      {o.ownerOnly && !isOwner ? ' Owner-only.' : ''}
                      {o.adminOnly && !isAdmin ? ' Admin-only.' : ''}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {hasDriver ? (
          <Field label="Engagement" htmlFor="invEngagement">
            <select
              id="invEngagement"
              value={engagement}
              onChange={(e) => setEngagement(e.target.value as DriverEngagementType)}
              className={cn(
                'block w-full rounded-md bg-white px-3 h-10 text-sm text-breaker-ink',
                'border border-breaker-line-2',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breaker-identity',
              )}
            >
              <option value="subcontractor">Subcontractor — needs cover on record</option>
              <option value="employed">Employed</option>
            </select>
          </Field>
        ) : null}

        {error ? (
          <div className="rounded-md bg-breaker-red/10 border border-breaker-red/30 px-3 py-2 text-sm text-breaker-red-deep">
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button type="submit" size="sm" loading={busy}>
            Send invite
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
// ============================================================
// Layout primitives
// ============================================================

function Card({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="surface rounded-lg overflow-hidden">
      <header className="px-5 py-3 border-b border-breaker-line bg-breaker-ground-2 flex items-center justify-between gap-2">
        <h2 className="text-2xs font-medium uppercase tracking-wider text-breaker-ink-2">
          {title}
        </h2>
        {action}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function DetailRow({
  label,
  children,
  last,
}: {
  label: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between py-2.5 gap-3',
        !last && 'border-b border-breaker-line',
      )}
    >
      <span className="text-xs text-breaker-mute shrink-0">{label}</span>
      <span className="text-sm text-right break-all">{children}</span>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        'px-2 py-2 text-2xs font-medium uppercase tracking-wider text-breaker-ink-2 first:pl-0 last:pr-0',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <td className={cn('px-2 py-3 align-middle first:pl-0 last:pr-0', className)}>
      {children}
    </td>
  );
}

// ============================================================
// Features (0061) — the per-fleet feature panel. Bays/social/TMS are
// platform-managed entitlements (read-only here); the customer portal
// toggle is the fleet's own call and moved here from Portal & brand.
// ============================================================

function FeaturesSettings({ fleet, canEdit }: { fleet: Fleet; canEdit: boolean }) {
  const update = useUpdateFleet(fleet.id);
  const toast = useToast();
  // Unit D rule 5: re-entrancy guard — a second change while one is saving is dropped.
  const toggleLock = useActionLock();
  const e = fleet.entitlements;

  const platformRows: Array<{ label: string; hint: string; on: boolean }> = [
    { label: 'TMS', hint: 'Loads, drivers, and dispatch.', on: e.tms },
    { label: 'Bays', hint: 'Community bay map.', on: e.bays },
    { label: 'Social', hint: 'Community feed.', on: e.social },
  ];

  return (
    <div className="space-y-3">
      {platformRows.map((r) => (
        <div key={r.label} className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-breaker-ink">{r.label}</p>
            <p className="mt-0.5 text-xs text-breaker-mute">{r.hint}</p>
          </div>
          <Badge tone={r.on ? 'success' : 'neutral'}>{r.on ? 'On' : 'Off'}</Badge>
        </div>
      ))}

      <div className="flex items-start justify-between gap-4 border-t border-breaker-line pt-3">
        <div>
          <p className="text-sm font-medium text-breaker-ink">Customer booking page</p>
          <p className="mt-0.5 text-xs text-breaker-mute">
            Customers sign in to book, track, and download invoices.
          </p>
        </div>
        {canEdit ? (
          <input
            type="checkbox"
            className="h-4 w-4 mt-1"
            checked={e.customerPortal}
            disabled={update.isPending}
            onChange={(ev) => {
              const next = ev.target.checked;
              toggleLock.run(() =>
                update
                  .mutateAsync({ hasCustomerPortal: next })
                  .then(() => toast.success('Booking page setting saved.'))
                  .catch(() => toast.error('Could not save.')),
              );
            }}
          />
        ) : (
          <Badge tone={e.customerPortal ? 'success' : 'neutral'}>
            {e.customerPortal ? 'On' : 'Off'}
          </Badge>
        )}
      </div>
      <p className="text-xs text-breaker-mute">
        TMS, Bays, and Social are platform-managed — talk to Breaker to change
        them.
      </p>
    </div>
  );
}

// ============================================================
// Billing settings — first writable fleet setting (S68).
// The toggle saves immediately; owner-gated (mirrors the server's
// requireFleetRole(['owner']) on PATCH /:fleetId).
// ============================================================

function BillingSettings({
  fleetId,
  vatRegistered,
  canEdit,
}: {
  fleetId: string;
  vatRegistered: boolean;
  canEdit: boolean;
}) {
  const update = useUpdateFleet(fleetId);
  const toast = useToast();
  // Unit D rule 5: re-entrancy guard — a second change while one is saving is dropped.
  const toggleLock = useActionLock();

  function onToggle(next: boolean) {
    toggleLock.run(() =>
      update
        .mutateAsync({ vatRegistered: next })
        .then(() =>
          toast.success(
            next
              ? 'VAT registered — new loads default to +20% VAT.'
              : 'Not VAT registered — new loads default to no VAT.',
          ),
        )
        .catch(() => toast.error('Could not save. Try again in a moment.')),
    );
  }

  if (!canEdit) {
    return (
      <DetailRow label="VAT registered" last>
        {vatRegistered ? 'Yes' : 'No'}
      </DetailRow>
    );
  }

  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div>
        <p className="text-sm font-medium text-breaker-ink">VAT registered</p>
        <p className="mt-0.5 text-xs text-breaker-mute">
          New loads default to +20% VAT when on. You can still toggle VAT per
          load.
        </p>
      </div>
      <label className="inline-flex cursor-pointer items-center">
        <input
          type="checkbox"
          checked={vatRegistered}
          disabled={update.isPending}
          onChange={(e) => onToggle(e.target.checked)}
          className="h-4 w-4"
        />
      </label>
    </div>
  );
}

// ============================================================
// Company settings (U8): the customer-facing identity block.
// Owner-gated like the PATCH it writes to.
// ============================================================

/** '' ⇄ null: inputs hold strings, nullable columns hold null. */
const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

function CompanySettings({ fleet, canEdit }: { fleet: Fleet; canEdit: boolean }) {
  const toast = useToast();
  const update = useUpdateFleet(fleet.id);
  // Unit D: Enter-key re-submits are dropped while one is in flight.
  const submitLock = useActionLock();
  const [form, setForm] = useState({
    brandName: fleet.brandName ?? '',
    legalName: fleet.legalName,
    companyNumber: fleet.companyNumber ?? '',
    phoneE164: fleet.phoneE164 ?? '',
    supportEmail: fleet.supportEmail ?? '',
    vatNumber: fleet.vatNumber ?? '',
    addressLine1: fleet.addressLine1 ?? '',
    addressLine2: fleet.addressLine2 ?? '',
    city: fleet.city ?? '',
    postcode: fleet.postcode ?? '',
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  if (!canEdit) {
    return (
      <>
        <DetailRow label="Brand name">{fleet.brandName ?? '—'}</DetailRow>
        <DetailRow label="Legal name">{fleet.legalName}</DetailRow>
        <DetailRow label="Company number">{fleet.companyNumber ?? '—'}</DetailRow>
        <DetailRow label="Phone">{fleet.phoneE164 ?? '—'}</DetailRow>
        <DetailRow label="Support email">{fleet.supportEmail ?? '—'}</DetailRow>
        <DetailRow label="Address" last>
          {[fleet.addressLine1, fleet.addressLine2, fleet.city, fleet.postcode]
            .filter(Boolean)
            .join(', ') || '—'}
        </DetailRow>
      </>
    );
  }

  function save(e: FormEvent) {
    e.preventDefault();
    if (!form.legalName.trim()) {
      toast.error('Legal name is required.');
      return;
    }
    submitLock.run(() =>
      update
        .mutateAsync({
          brandName: orNull(form.brandName),
          legalName: form.legalName.trim(),
          companyNumber: orNull(form.companyNumber),
          vatNumber: orNull(form.vatNumber),
          phoneE164: orNull(form.phoneE164),
          supportEmail: orNull(form.supportEmail),
          addressLine1: orNull(form.addressLine1),
          addressLine2: orNull(form.addressLine2),
          city: orNull(form.city),
          postcode: orNull(form.postcode),
        })
        .then(() => toast.success('Company details saved.'))
        .catch((err) =>
          toast.error(err instanceof ApiError ? err.message : 'Could not save.'),
        ),
    );
  }

  return (
    <form onSubmit={save} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="Brand name"
          htmlFor="coBrand"
          hint="What customers see — portal, emails, invoices."
        >
          <Input id="coBrand" value={form.brandName} onChange={set('brandName')} placeholder={fleet.tradingName ?? fleet.legalName} />
        </Field>
        <Field label="Legal name" htmlFor="coLegal">
          <Input id="coLegal" value={form.legalName} onChange={set('legalName')} />
        </Field>
        <Field label="Company number" htmlFor="coNumber">
          <Input id="coNumber" value={form.companyNumber} onChange={set('companyNumber')} />
        </Field>
        {/* M2a: a VAT-registered fleet CANNOT issue an invoice without this —
            the database refuses it (BR104), and the number prints on every
            VAT invoice. Until now there was no field for it anywhere in the
            app, so the refusal pointed at a page that could not satisfy it. */}
        <Field
          label="VAT number"
          htmlFor="coVat"
          hint="Required to issue VAT invoices. Prints on every invoice."
        >
          <Input
            id="coVat"
            value={form.vatNumber}
            onChange={set('vatNumber')}
            placeholder="GB123456789"
          />
        </Field>
        <Field label="Phone (E.164)" htmlFor="coPhone">
          <Input id="coPhone" value={form.phoneE164} onChange={set('phoneE164')} placeholder="+441912345678" />
        </Field>
        <Field label="Support email" htmlFor="coSupport">
          <Input id="coSupport" type="email" value={form.supportEmail} onChange={set('supportEmail')} />
        </Field>
        <Field label="Address line 1" htmlFor="coAddr1">
          <Input id="coAddr1" value={form.addressLine1} onChange={set('addressLine1')} />
        </Field>
        <Field label="Address line 2" htmlFor="coAddr2">
          <Input id="coAddr2" value={form.addressLine2} onChange={set('addressLine2')} />
        </Field>
        <Field label="City" htmlFor="coCity">
          <Input id="coCity" value={form.city} onChange={set('city')} />
        </Field>
        <Field label="Postcode" htmlFor="coPostcode">
          <Input id="coPostcode" value={form.postcode} onChange={set('postcode')} />
        </Field>
      </div>
      <div className="flex justify-end">
        <Button type="submit" size="sm" loading={update.isPending || submitLock.locked}>
          Save company details
        </Button>
      </div>
    </form>
  );
}

// ============================================================
// Portal & brand (U8): portal on/off, subdomain, logo, colours,
// read-only mail identity.
// ============================================================

/** The portal subdomain editor — extracted from the old PortalBrandSettings
 *  (brand colours/logo + mail identity now live on /settings/brand). Lands
 *  on /settings/domains as the free-subdomain address control. */
function PortalSlugSettings({
  fleet,
  canEdit,
}: {
  fleet: Fleet;
  canEdit: boolean;
}) {
  const toast = useToast();
  const setSlug = useSetPortalSlug(fleet.id);
  const [slug, setSlugInput] = useState(fleet.portalSlug ?? '');

  const slugHost = slug.trim()
    ? `${slug.trim().toLowerCase()}.portal.breakernetwork.app`
    : null;

  function saveSlug() {
    return setSlug
      .mutateAsync(orNull(slug.toLowerCase()))
      .then((data) =>
        toast.success(
          data.fleet.portalSlug
            ? `Portal live at ${data.fleet.portalSlug}.portal.breakernetwork.app`
            : 'Portal subdomain cleared.',
        ),
      )
      .catch((err) =>
        toast.error(err instanceof ApiError ? err.message : 'Could not save subdomain.'),
      );
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-breaker-mute">
        Your customers book and track jobs here. Already have a website? Link
        to this page from it, or use a custom domain like
        portal.yourdomain.co.uk.
      </p>
      {!fleet.entitlements.customerPortal ? (
        <p className="text-sm text-breaker-ink bg-breaker-ground-2 border border-breaker-line rounded-md p-3">
          Booking page off. You can still create jobs in the dispatcher and
          send customers tracking links.
        </p>
      ) : null}

      <div>
        <Field
          label="Portal subdomain"
          htmlFor="portalSlug"
          hint={
            slugHost
              ? `Customers visit https://${slugHost}`
              : 'Lowercase letters, digits, and hyphens.'
          }
        >
          {/* flex-wrap: at 390px the suffix + Save drop to a second line
              instead of crushing the input. */}
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id="portalSlug"
              value={slug}
              onChange={(e) => setSlugInput(e.target.value)}
              placeholder="yourbrand"
              disabled={!canEdit}
              className="max-w-[220px] min-w-[140px] flex-1"
            />
            <span className="text-xs text-breaker-mute whitespace-nowrap">
              .portal.breakernetwork.app
            </span>
            {canEdit ? (
              <Button size="sm" variant="secondary" action={saveSlug}>
                Save
              </Button>
            ) : null}
          </div>
        </Field>
      </div>
    </div>
  );
}

// ============================================================
// Rate card (U9) — the editor itself lives in
// components/rate-card-editor.tsx (shared with the signup wizard).
// ============================================================

function RateCardSettings({ fleetId, canEdit }: { fleetId: string; canEdit: boolean }) {
  const tariffQuery = useTariff(fleetId);
  if (tariffQuery.isLoading || !tariffQuery.data) {
    return (
      <div className="py-6 flex justify-center">
        <Spinner />
      </div>
    );
  }
  const { tariff, defaults } = tariffQuery.data;
  const card: TariffDefaults = tariff ?? defaults;
  return (
    <RateCardEditor
      key={`${fleetId}:${tariff ? tariff.updatedAt : 'defaults'}`}
      fleetId={fleetId}
      canEdit={canEdit}
      stored={Boolean(tariff)}
      initial={seedTariffForm(card)}
    />
  );
}

// ---------------------------------------------------------------------------
// Custom domain (0065) — self-serve portal hostname, Resend-style: claim →
// publish two DNS records → verified → live. One custom host per fleet.
// ---------------------------------------------------------------------------

const DOMAIN_ERROR_GUIDANCE: Record<string, string> = {
  txt_missing:
    "We can't see the TXT record yet. DNS changes can take up to an hour to propagate — we re-check automatically, or press Check DNS.",
  txt_mismatch:
    "A TXT record exists but its value doesn't match your token. Copy the value below exactly (no quotes, no spaces).",
  routing_missing:
    'No CNAME (or A record) found for your hostname yet — add the CNAME below at your DNS host.',
  routing_wrong_target:
    'Your hostname points somewhere else — compare what we found with the expected target below.',
  cloudflare_proxied:
    "Your record is proxied through Cloudflare (orange cloud), which hides the CNAME from us. In Cloudflare's DNS page, click the orange cloud on this record so it turns grey ('DNS only'), then check again.",
  caa_blocks_issuance:
    'Your domain has CAA records that don\'t allow Let\'s Encrypt to issue certificates. Add a CAA record: 0 issue "letsencrypt.org".',
  txt_lapsed:
    'The ownership TXT record disappeared, so serving stopped. Restore the TXT record and press Check DNS.',
};

/** Outcomes whose fix-it copy names the exact record — built per-domain. */
function domainErrorGuidance(
  lastError: string | null,
  expected: ExpectedDnsRecords,
): string | null {
  if (!lastError) return null;
  if (lastError === 'txt_doubled_name') {
    return (
      `Your TXT record exists, but its Host was entered as the full hostname, so your ` +
      `DNS provider appended ${expected.zone} again. Edit that record's Host to just ` +
      `${expected.txtNameRelative} — the value is already correct.`
    );
  }
  if (lastError === 'routing_doubled_name') {
    return (
      `Your CNAME exists, but its Host was entered as the full hostname, so your ` +
      `DNS provider appended ${expected.zone} again. Edit that record's Host to just ` +
      `${expected.cnameNameRelative} — the target is already correct.`
    );
  }
  return DOMAIN_ERROR_GUIDANCE[lastError] ?? null;
}

function DomainStatusBadge({ status }: { status: 'pending' | 'verified' | 'live' }) {
  if (status === 'live') return <Badge tone="success">Live</Badge>;
  if (status === 'verified') return <Badge tone="info">Verified — issuing certificate</Badge>;
  return <Badge tone="neutral">Awaiting DNS</Badge>;
}

function CopyValue({ value }: { value: string }) {
  const toast = useToast();
  return (
    <button
      type="button"
      className="ref break-all text-left underline decoration-dotted underline-offset-2 hover:text-breaker-ink"
      title="Copy"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(
          () => toast.success('Copied'),
          () => toast.error('Copy failed'),
        );
      }}
    >
      {value}
    </button>
  );
}

function CustomDomainSettings({
  fleetId,
  canEdit,
}: {
  fleetId: string | null;
  canEdit: boolean;
}) {
  const view = usePortalDomains(fleetId);
  const claim = useClaimPortalDomain(fleetId);
  const check = useCheckPortalDomain(fleetId);
  const removeDomain = useRemovePortalDomain(fleetId);
  const toast = useToast();
  const [hostname, setHostname] = useState('');
  const [apexOffer, setApexOffer] = useState<string | null>(null);

  if (view.isLoading) {
    return (
      <div className="py-6 flex justify-center">
        <Spinner size="sm" />
      </div>
    );
  }

  const custom = view.data?.custom ?? null;
  const slugHost = view.data?.slugHostname ?? null;

  function submitClaim(allowApex: boolean) {
    const value = (apexOffer ?? hostname).trim().toLowerCase();
    if (!value) return Promise.resolve();
    return claim
      .mutateAsync({ hostname: value, allowApex })
      .then(() => {
        setHostname('');
        setApexOffer(null);
        toast.success('Domain claimed — now publish the two DNS records');
      })
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Claim failed';
        if (msg.includes('apex_use_subdomain')) {
          setApexOffer(value);
        } else {
          toast.error(msg);
        }
      });
  }

  if (!custom) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-breaker-mute">
          Enter the address your customers will use — your main site stays
          exactly as it is, and you add the two records we&rsquo;ll give you at
          your DNS provider.
          {slugHost ? (
            <>
              {' '}
              Your booking page stays available at{' '}
              <span className="ref">{slugHost}</span> alongside it.
            </>
          ) : null}
        </p>
        {canEdit ? (
          <div className="flex gap-2 items-end max-w-lg">
            <div className="flex-1">
              <Field label="Hostname" htmlFor="customDomain">
                <Input
                  id="customDomain"
                  value={hostname}
                  onChange={(e) => setHostname(e.target.value)}
                  placeholder="portal.yourdomain.co.uk"
                />
              </Field>
            </div>
            <Button
              size="sm"
              action={() => submitClaim(false)}
              loading={claim.isPending}
              disabled={hostname.trim().length < 4}
            >
              Connect
            </Button>
          </div>
        ) : (
          <p className="text-xs text-breaker-mute">Ask an admin or the owner to connect one.</p>
        )}
        {apexOffer ? (
          <div className="rounded-md border border-breaker-line bg-breaker-ground-2 p-3 space-y-2">
            <p className="text-sm text-breaker-ink">
              <span className="ref">{apexOffer}</span> is a root domain — most DNS hosts
              can&rsquo;t point a CNAME at the root. We recommend{' '}
              <span className="ref">portal.{apexOffer}</span> instead.
            </p>
            <p className="text-xs text-breaker-mute">
              A root A record to our IP works but is fragile: if our IP ever changes, your
              portal breaks silently until you update it.
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setHostname(`portal.${apexOffer}`);
                  setApexOffer(null);
                }}
              >
                Use portal.{apexOffer}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                action={() => submitClaim(true)}
                loading={claim.isPending}
              >
                Use the root anyway
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  const observed = (custom.dnsObserved ?? {}) as {
    txt?: string[];
    cname?: string | null;
    a?: string[];
  };
  const guidance = domainErrorGuidance(custom.lastError, custom.expected);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="ref text-sm">{custom.hostname}</span>
        <DomainStatusBadge status={custom.status} />
        {custom.lastCheckedAt ? (
          <span className="text-2xs text-breaker-mute">
            checked {formatRelative(custom.lastCheckedAt)}
          </span>
        ) : null}
      </div>

      {custom.status !== 'live' ? (
        <>
          <p className="text-sm text-breaker-mute">
            Publish both records at your DNS host, then press Check DNS. Changes usually
            appear within minutes but can take up to an hour — we also re-check
            automatically every hour.
          </p>
          <TableScroll>
            <table className="w-full text-sm">
              <thead className="border-b border-breaker-line text-left">
                <tr>
                  <th className="py-2 pr-3 font-medium text-breaker-ink-2 w-[70px]">Type</th>
                  <th className="py-2 pr-3 font-medium text-breaker-ink-2">Name</th>
                  <th className="py-2 font-medium text-breaker-ink-2">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-breaker-line align-top">
                  <td className="py-2 pr-3">TXT</td>
                  <td className="py-2 pr-3">
                    <CopyValue value={custom.expected.txtNameRelative} />
                  </td>
                  <td className="py-2">
                    <CopyValue value={custom.expected.txtValue ?? ''} />
                  </td>
                </tr>
                <tr className="align-top">
                  <td className="py-2 pr-3">CNAME</td>
                  <td className="py-2 pr-3">
                    <CopyValue value={custom.expected.cnameNameRelative} />
                  </td>
                  <td className="py-2">
                    {custom.expected.cnameTarget ? (
                      <CopyValue value={custom.expected.cnameTarget} />
                    ) : (
                      <span className="text-breaker-mute">
                        set a portal subdomain first (Portal &amp; brand)
                      </span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </TableScroll>
          <p className="text-2xs text-breaker-mute">
            Names are relative to <span className="ref">{custom.expected.zone}</span> —
            most DNS dashboards append it automatically, so enter them exactly as shown.
            If your provider asks for full hostnames, use{' '}
            <span className="ref break-all">{custom.expected.txtName}</span> and{' '}
            <span className="ref break-all">{custom.expected.cnameName}</span>.
          </p>
        </>
      ) : (
        <p className="text-sm text-breaker-mute">
          Your portal is live on <span className="ref">https://{custom.hostname}</span>
          {slugHost ? (
            <>
              {' '}
              — <span className="ref">{slugHost}</span> keeps working alongside it.
            </>
          ) : null}{' '}
          Keep both DNS records in place; we re-verify ownership daily.
        </p>
      )}

      {guidance ? (
        <div className="rounded-md border border-breaker-line bg-breaker-ground-2 p-3 space-y-2">
          <p className="text-sm text-breaker-ink">{guidance}</p>
          {(observed.txt?.length || observed.cname || observed.a?.length) ? (
            <p className="text-2xs text-breaker-mute break-all">
              Found:{' '}
              {[
                observed.txt?.length ? `TXT ${observed.txt.join(' | ')}` : null,
                observed.cname ? `CNAME → ${observed.cname}` : null,
                observed.a?.length ? `A ${observed.a.join(', ')}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          ) : (
            <p className="text-2xs text-breaker-mute">Found: no records yet.</p>
          )}
        </div>
      ) : null}

      {canEdit ? (
        <div className="flex gap-2">
          {custom.status !== 'live' ? (
            <Button
              size="sm"
              action={() =>
                check
                  .mutateAsync(undefined)
                  .then((r) => {
                    if (r.domain.status === 'live') toast.success('Live — certificate issued');
                    else if (r.domain.status === 'verified')
                      toast.success('Verified — issuing the certificate now');
                    else toast.info('Not visible yet — see what we found');
                  })
                  .catch((e) =>
                    toast.error(e instanceof Error ? e.message : 'Check failed'),
                  )
              }
            >
              Check DNS
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            className="text-breaker-red hover:text-breaker-red-deep hover:bg-breaker-red/10"
            action={() =>
              removeDomain
                .mutateAsync(custom.hostname)
                .then(() => toast.success('Domain removed — the hostname is free again'))
                .catch((e) => toast.error(e instanceof Error ? e.message : 'Remove failed'))
            }
          >
            Remove
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// FAD A1: the admin counterpart of CustomDomainSettings — one derived
// hostname (admin.<zone>), CNAME-only when the zone is already proven by
// the custom domain above, full TXT rail otherwise. Central
// app.breakernetwork.app keeps working either way.
function AdminDomainSettings({
  fleetId,
  canEdit,
}: {
  fleetId: string | null;
  canEdit: boolean;
}) {
  const view = usePortalDomains(fleetId);
  const claim = useClaimAdminDomain(fleetId);
  const check = useCheckAdminDomain(fleetId);
  const removeDomain = useRemovePortalDomain(fleetId);
  const toast = useToast();

  if (view.isLoading) {
    return (
      <div className="py-6 flex justify-center">
        <Spinner size="sm" />
      </div>
    );
  }

  const admin = view.data?.admin ?? null;
  const suggestion = view.data?.adminSuggestion ?? null;

  if (!admin) {
    if (!suggestion) {
      return (
        <p className="text-sm text-breaker-mute">
          Connect a custom domain above first — your admin address lives on the
          same domain, like <span className="ref">admin.yourdomain.co.uk</span>.
          Your team signs in at <span className="ref">app.breakernetwork.app</span>{' '}
          either way.
        </p>
      );
    }
    return (
      <div className="space-y-3">
        <p className="text-sm text-breaker-mute">
          Give your team their own sign-in address:{' '}
          <span className="ref">{suggestion}</span>. One DNS record, and{' '}
          <span className="ref">app.breakernetwork.app</span> keeps working
          alongside it.
        </p>
        {canEdit ? (
          <Button
            size="sm"
            action={() =>
              claim
                .mutateAsync()
                .then((r) => {
                  toast.success(
                    r.domain.expected.mode === 'cname_only'
                      ? 'Claimed — one CNAME record and it goes live'
                      : 'Claimed — now publish the two DNS records',
                  );
                })
                .catch((e) =>
                  toast.error(e instanceof Error ? e.message : 'Claim failed'),
                )
            }
            loading={claim.isPending}
          >
            Use {suggestion}
          </Button>
        ) : (
          <p className="text-xs text-breaker-mute">Ask an admin or the owner to set it up.</p>
        )}
      </div>
    );
  }

  const observed = (admin.dnsObserved ?? {}) as {
    txt?: string[];
    cname?: string | null;
    a?: string[];
  };
  const cnameOnly = admin.expected.mode === 'cname_only';
  const guidance = domainErrorGuidance(admin.lastError, admin.expected);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="ref text-sm">{admin.hostname}</span>
        <DomainStatusBadge status={admin.status} />
        {admin.lastCheckedAt ? (
          <span className="text-2xs text-breaker-mute">
            checked {formatRelative(admin.lastCheckedAt)}
          </span>
        ) : null}
      </div>

      {admin.status !== 'live' ? (
        <>
          <p className="text-sm text-breaker-mute">
            {cnameOnly ? (
              <>
                Your domain is already verified, so one CNAME record is all this
                needs. Publish it, then press Check DNS.
              </>
            ) : (
              <>
                Publish both records at your DNS host, then press Check DNS. Changes
                usually appear within minutes but can take up to an hour — we also
                re-check automatically every hour.
              </>
            )}
          </p>
          <TableScroll>
            <table className="w-full text-sm">
              <thead className="border-b border-breaker-line text-left">
                <tr>
                  <th className="py-2 pr-3 font-medium text-breaker-ink-2 w-[70px]">Type</th>
                  <th className="py-2 pr-3 font-medium text-breaker-ink-2">Name</th>
                  <th className="py-2 font-medium text-breaker-ink-2">Value</th>
                </tr>
              </thead>
              <tbody>
                {!cnameOnly ? (
                  <tr className="border-b border-breaker-line align-top">
                    <td className="py-2 pr-3">TXT</td>
                    <td className="py-2 pr-3">
                      <CopyValue value={admin.expected.txtNameRelative} />
                    </td>
                    <td className="py-2">
                      <CopyValue value={admin.expected.txtValue ?? ''} />
                    </td>
                  </tr>
                ) : null}
                <tr className="align-top">
                  <td className="py-2 pr-3">CNAME</td>
                  <td className="py-2 pr-3">
                    <CopyValue value={admin.expected.cnameNameRelative} />
                  </td>
                  <td className="py-2">
                    {admin.expected.cnameTarget ? (
                      <CopyValue value={admin.expected.cnameTarget} />
                    ) : (
                      <span className="text-breaker-mute">
                        set a portal subdomain first (Portal &amp; brand)
                      </span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </TableScroll>
          <p className="text-2xs text-breaker-mute">
            Names are relative to <span className="ref">{admin.expected.zone}</span> —
            most DNS dashboards append it automatically, so enter them exactly as
            shown. If your provider asks for full hostnames, use{' '}
            <span className="ref break-all">{admin.expected.cnameName}</span>
            {!cnameOnly ? (
              <>
                {' '}
                and <span className="ref break-all">{admin.expected.txtName}</span>
              </>
            ) : null}
            .
          </p>
        </>
      ) : (
        <p className="text-sm text-breaker-mute">
          Your team signs in at <span className="ref">https://{admin.hostname}</span>{' '}
          — <span className="ref">app.breakernetwork.app</span> keeps working
          alongside it.
        </p>
      )}

      {guidance ? (
        <div className="rounded-md border border-breaker-line bg-breaker-ground-2 p-3 space-y-2">
          <p className="text-sm text-breaker-ink">{guidance}</p>
          {(observed.txt?.length || observed.cname || observed.a?.length) ? (
            <p className="text-2xs text-breaker-mute break-all">
              Found:{' '}
              {[
                observed.txt?.length ? `TXT ${observed.txt.join(' | ')}` : null,
                observed.cname ? `CNAME → ${observed.cname}` : null,
                observed.a?.length ? `A ${observed.a.join(', ')}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          ) : (
            <p className="text-2xs text-breaker-mute">Found: no records yet.</p>
          )}
        </div>
      ) : null}

      {canEdit ? (
        <div className="flex gap-2">
          {admin.status !== 'live' ? (
            <Button
              size="sm"
              action={() =>
                check
                  .mutateAsync(undefined)
                  .then((r) => {
                    if (r.domain.status === 'live') toast.success('Live — certificate issued');
                    else if (r.domain.status === 'verified')
                      toast.success('Verified — issuing the certificate now');
                    else toast.info('Not visible yet — see what we found');
                  })
                  .catch((e) =>
                    toast.error(e instanceof Error ? e.message : 'Check failed'),
                  )
              }
            >
              Check DNS
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            className="text-breaker-red hover:text-breaker-red-deep hover:bg-breaker-red/10"
            action={() =>
              removeDomain
                .mutateAsync(admin.hostname)
                .then(() => toast.success('Admin domain removed — sign-in moves back to app.breakernetwork.app'))
                .catch((e) => toast.error(e instanceof Error ? e.message : 'Remove failed'))
            }
          >
            Remove
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ============================================================
// Your portal address — the three doors (U6; approved copy in
// docs/domain-purchase-drafts/fleet-settings-three-door-copy.md).
// Order matters: the free subdomain stays first and stays the celebrated
// default — the paid door is an offer, not an upsell wall.
// ============================================================

function formatPrice(pence: number): string {
  return pence % 100 === 0 ? `£${pence / 100}` : `£${(pence / 100).toFixed(2)}`;
}

function brandSeed(fleet: Fleet): string {
  return (fleet.tradingName ?? fleet.legalName)
    .toLowerCase()
    .replace(/\b(ltd|limited|plc)\b\.?/g, '')
    .trim();
}

function PortalAddressSettings({
  fleet,
  fleetId,
  canEdit,
}: {
  fleet: Fleet;
  fleetId: string | null;
  canEdit: boolean;
}) {
  const view = usePortalDomains(fleetId);
  const config = useDomainOrdersConfig(fleetId);
  const orders = useDomainOrders(fleetId);
  const [door, setDoor] = useState<'none' | 'connect' | 'get'>('none');

  if (view.isLoading) {
    return (
      <div className="py-6 flex justify-center">
        <Spinner size="sm" />
      </div>
    );
  }

  const custom = view.data?.custom ?? null;
  const slugHost = view.data?.slugHostname ?? null;

  if (custom?.managed) {
    return <ManagedDomainPanel domain={custom} fleetId={fleetId} canEdit={canEdit} />;
  }
  if (custom) {
    // Door 2, connected: the existing 0065 rail owns the claimed state.
    return <CustomDomainSettings fleetId={fleetId} canEdit={canEdit} />;
  }

  // A purchase mid-flight (or just failed) replaces the doors with honesty.
  const latest = (orders.data ?? []).find((o) => o.kind === 'register') ?? null;
  const cameFromCheckout =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('domain') === 'processing';
  if (
    latest &&
    (latest.status === 'fulfilling' ||
      latest.status === 'pending_operation' ||
      (latest.status === 'awaiting_payment' && cameFromCheckout))
  ) {
    return (
      <div className="rounded-md border border-breaker-line bg-breaker-ground-2 p-4 space-y-1">
        <p className="text-sm font-medium text-breaker-ink">
          Registering {latest.domain} with the registry — usually a minute or two.
        </p>
        <p className="text-sm text-breaker-mute">
          We&rsquo;ll set up your portal, admin address and email records the moment it
          lands. Safe to leave this page.
        </p>
      </div>
    );
  }
  const justFailed =
    latest && (latest.status === 'failed' || latest.status === 'refunded');

  const tlds = config.data?.tlds ?? [];
  const prices = [...new Set(tlds.map((t) => t.retailPence))];
  const priceLine =
    prices.length === 1 ? formatPrice(prices[0]!) : prices.length > 1 ? `from ${formatPrice(Math.min(...prices))}` : null;
  const seed = brandSeed(fleet).replace(/[^a-z0-9]+/g, '');
  const exampleDomain = `${seed || 'yourfleet'}.co.uk`;

  return (
    <div className="space-y-4">
      <p className="text-sm text-breaker-mute">
        Where your customers book. You&rsquo;ve already got a good one — the other two
        doors are for when you want your own name on it.
      </p>

      {justFailed ? (
        <div className="rounded-md border border-breaker-line bg-breaker-ground-2 p-3 space-y-1">
          <p className="text-sm text-breaker-ink">
            We couldn&rsquo;t register {latest!.domain} — sometimes a name is snapped up
            between search and registry. Your payment has been refunded in full (arrives
            in 5–10 working days). Fancy another try?
          </p>
        </div>
      ) : null}

      {/* Door 1 — the free subdomain (default, celebrated) */}
      <div className="rounded-md border border-breaker-line p-4 space-y-1">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="ref text-sm">{slugHost ?? 'set a portal subdomain (Portal & brand)'}</span>
          {slugHost ? <Badge tone="success">Live · yours free, forever</Badge> : null}
        </div>
        <p className="text-sm text-breaker-mute">
          Ready the moment you picked your name — certificate, tracking links, the lot.
          Plenty of fleets run on their subdomain for good; there&rsquo;s nothing
          second-class about it.
        </p>
      </div>

      {/* Door 2 — connect your own */}
      <div className="rounded-md border border-breaker-line p-4 space-y-2">
        <p className="text-sm font-medium text-breaker-ink">Already own a domain?</p>
        <p className="text-sm text-breaker-mute">
          Point it at Breaker and we&rsquo;ll walk you through the two DNS records, check
          them for you, and issue the certificate. Most fleets use a subdomain of their
          existing website, like <span className="ref">portal.{exampleDomain}</span>.
        </p>
        {door === 'connect' ? (
          <CustomDomainSettings fleetId={fleetId} canEdit={canEdit} />
        ) : canEdit ? (
          <Button size="sm" variant="secondary" onClick={() => setDoor('connect')}>
            Connect your domain
          </Button>
        ) : null}
      </div>

      {/* Door 3 — get a domain */}
      {config.data?.enabled && priceLine ? (
        <div className="rounded-md border border-breaker-line p-4 space-y-2">
          <p className="text-sm font-medium text-breaker-ink">Want your own domain?</p>
          <p className="text-sm text-breaker-mute">
            Get <span className="ref">{exampleDomain}</span> from right here — {priceLine} a
            year, flat. We register it in your name, set up everything (portal, your own
            admin address, email records), and renew it for the same price. No transfer
            fees if you ever leave.
          </p>
          {door === 'get' ? (
            <GetDomainFlow fleet={fleet} fleetId={fleetId} />
          ) : canEdit ? (
            <Button size="sm" onClick={() => setDoor('get')}>
              Find your domain
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ManagedDomainPanel({
  domain,
  fleetId,
  canEdit,
}: {
  domain: PortalDomain & { expected: ExpectedDnsRecords };
  fleetId: string | null;
  canEdit: boolean;
}) {
  const zone = domain.registrarDomain ?? domain.expected.zone;
  const renews = domain.registrarExpiresAt
    ? new Date(domain.registrarExpiresAt).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="ref text-sm">{zone}</span>
        <DomainStatusBadge status={domain.status} />
        <Badge tone="info">Yours — managed by Breaker</Badge>
      </div>
      {domain.status === 'live' ? (
        <p className="text-sm text-breaker-mute">
          Your customers book at <span className="ref">https://{domain.hostname}</span> —
          your team signs in at <span className="ref">https://admin.{zone}</span>.
        </p>
      ) : (
        <p className="text-sm text-breaker-mute">
          Portal: <span className="ref">{domain.hostname}</span> · Admin:{' '}
          <span className="ref">admin.{zone}</span> — records planted, certificate on its
          way. Your customers&rsquo; links keep working throughout.
        </p>
      )}
      {renews ? (
        <p className="text-2xs text-breaker-mute">
          Renews {renews} — same flat price, and we&rsquo;ll remind you first. Want to
          move it to your own registrar? Ask support for the transfer code any time, no
          fees.
        </p>
      ) : null}
      {fleetId && zone ? (
        <ManagedDomainDns fleetId={fleetId} zone={zone} canEdit={canEdit} />
      ) : null}
    </div>
  );
}

function GetDomainFlow({ fleet, fleetId }: { fleet: Fleet; fleetId: string | null }) {
  // 0085 — the founding comp entitlement. False for every fleet that never
  // redeemed a code, so this door renders exactly as it always has.
  const domainOrdersConfig = useDomainOrdersConfig(fleetId);
  const compIncluded = domainOrdersConfig.data?.compEntitlement.available ?? false;
  const toast = useToast();
  const search = useDomainSearch(fleetId);
  const createOrder = useCreateDomainOrder(fleetId);
  const [query, setQuery] = useState(brandSeed(fleet));
  const [results, setResults] = useState<DomainCandidate[] | null>(null);
  const [parsed, setParsed] = useState<DomainSearchParsed | null>(null);
  const [chosen, setChosen] = useState<DomainCandidate | null>(null);
  const [registrant, setRegistrant] = useState<DomainRegistrantInput>({
    firstName: '',
    lastName: '',
    email: '',
    address1: '',
    city: '',
    postalCode: '',
    phone: '',
  });
  const [organization, setOrganization] = useState('');
  const [registrationConsent, setRegistrationConsent] = useState(false);
  const [whoisPrivacy, setWhoisPrivacy] = useState(true);

  function runSearch() {
    search
      .mutateAsync(query)
      .then((r) => {
        setResults(r.candidates);
        setParsed(r.parsed);
        setChosen(null);
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Search failed'));
  }

  function pay() {
    if (!chosen) return;
    createOrder
      .mutateAsync({
        domain: chosen.domain,
        registrant: {
          ...registrant,
          ...(organization.trim() ? { organization: organization.trim() } : {}),
        },
        registrationConsent,
        whoisPrivacy,
      })
      .then((r) => {
        // 0085: a comp order has no card step — it is already fulfilling, so
        // stay put and let the orders panel poll it to done.
        if (r.checkoutUrl) {
          window.location.assign(r.checkoutUrl);
        }
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Could not start payment'));
  }

  const formReady =
    registrationConsent &&
    registrant.firstName.trim() &&
    registrant.lastName.trim() &&
    registrant.email.trim() &&
    registrant.address1.trim() &&
    registrant.city.trim() &&
    registrant.postalCode.trim() &&
    registrant.phone.trim();

  return (
    <div className="space-y-4 pt-1">
      {/* Step 1 — search, seeded from the fleet brand name. */}
      <div className="space-y-2 max-w-lg">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Field label="Search for a name" htmlFor="domainSeed">
              <Input
                id="domainSeed"
                value={query}
                onChange={(e) => setQuery(e.target.value.toLowerCase())}
                placeholder="harbourcouriers or harbourcouriers.co.uk"
                {...domainQueryInputProps}
              />
            </Field>
          </div>
          <Button size="sm" onClick={runSearch} loading={search.isPending} disabled={query.trim().length < 2}>
            Search
          </Button>
        </div>
        <TldPriceChips fleetId={fleetId} />
      </div>

      {results ? (
        <div className="max-w-lg">
          <DomainResultsList
            results={results}
            parsed={parsed}
            chosen={chosen}
            onChoose={setChosen}
            compIncluded={compIncluded}
          />
        </div>
      ) : null}

      {/* Step 2 — registrant details + consent. */}
      {chosen ? (
        <div className="space-y-3 border-t border-breaker-line pt-3">
          <p className="text-sm text-breaker-mute italic">
            Domains are registered to a real person or business — the registry requires
            it. This registers <span className="not-italic font-medium">you</span>, not
            us, as the owner.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
            <Field label="First name" htmlFor="regFirst">
              <Input
                id="regFirst"
                value={registrant.firstName}
                onChange={(e) => setRegistrant({ ...registrant, firstName: e.target.value })}
              />
            </Field>
            <Field label="Last name" htmlFor="regLast">
              <Input
                id="regLast"
                value={registrant.lastName}
                onChange={(e) => setRegistrant({ ...registrant, lastName: e.target.value })}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Business name (optional)" htmlFor="regOrg">
                <Input
                  id="regOrg"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Email" htmlFor="regEmail">
                <Input
                  id="regEmail"
                  type="email"
                  value={registrant.email}
                  onChange={(e) => setRegistrant({ ...registrant, email: e.target.value })}
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Address" htmlFor="regAddr">
                <Input
                  id="regAddr"
                  value={registrant.address1}
                  onChange={(e) => setRegistrant({ ...registrant, address1: e.target.value })}
                />
              </Field>
            </div>
            <Field label="City" htmlFor="regCity">
              <Input
                id="regCity"
                value={registrant.city}
                onChange={(e) => setRegistrant({ ...registrant, city: e.target.value })}
              />
            </Field>
            <Field label="Postcode" htmlFor="regPostcode">
              <Input
                id="regPostcode"
                value={registrant.postalCode}
                onChange={(e) => setRegistrant({ ...registrant, postalCode: e.target.value })}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Phone" htmlFor="regPhone">
                <Input
                  id="regPhone"
                  value={registrant.phone}
                  onChange={(e) => setRegistrant({ ...registrant, phone: e.target.value })}
                  placeholder="07… or +44 both fine"
                />
              </Field>
              <p className="text-2xs text-breaker-mute mt-1">
                UK numbers in any format — 07… or +44 both fine.
              </p>
            </div>
          </div>

          {/* Required registration consent (gates Pay), split from the
              optional, TLD-honest WHOIS-privacy preference. */}
          <div className="space-y-2 max-w-lg">
            <RegistrationTermsConsent
              domain={chosen.domain}
              checked={registrationConsent}
              onChange={setRegistrationConsent}
            />
            <WhoisPrivacyControl
              domain={chosen.domain}
              value={whoisPrivacy}
              onChange={setWhoisPrivacy}
            />
          </div>

          {/* Step 3 — pay (Stripe Checkout handles the card screen), unless
              0085's comp entitlement covers it and there is no card step. */}
          <div className="space-y-1">
            <Button onClick={pay} loading={createOrder.isPending} disabled={!formReady}>
              {compIncluded ? (
                <>Get {chosen.domain} — included</>
              ) : (
                <>
                  Pay {chosen.retailPence !== null ? formatPrice(chosen.retailPence) : ''} — get{' '}
                  {chosen.domain}
                </>
              )}
            </Button>
            <p className="text-2xs text-breaker-mute">
              {compIncluded
                ? "This one's included with your founding code — nothing to pay. It renews free each year while your account is active, and we'll always tell you before it does."
                : 'One payment covers the year. We\u2019ll remind you before each renewal — same price, never silent.'}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
